import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { usePanel } from './api';

/**
 * Panel — los indicadores transversales del portal.
 *
 * ## Lo que cambia
 *
 * - **Las barras de cobertura ya no dicen el dato solo con el color.** Eran
 *   tres hexadecimales según el porcentaje —verde, naranja, rojo— y el número
 *   al lado en ese mismo color. Ahora el número va en tinta legible y la barra
 *   es la del sistema; quien no distingue los colores sigue leyendo el mismo
 *   porcentaje.
 * - **La fecha de la última importación se ve en la hora de quien mira**: era
 *   `ultima.replace('T',' ').slice(0,16)`, o sea UTC en crudo.
 * - Los treinta y un números son los mismos, agrupados igual.
 */
export function Panel() {
  const consulta = usePanel(true);
  const d = consulta.data;

  if (consulta.isPending) return <SkeletonTarjetas n={6} />;
  if (consulta.isError || !d) {
    return (
      <EstadoError
        err={consulta.error}
        que="el panel de administración"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Grupo icono="bi-people" titulo="Colaboradores y gobierno del dato">
        <Cifras datos={[
          ['Activos', d.colaboradores.activos],
          ['Inactivos', d.colaboradores.inactivos],
          ['Hijos registrados', d.colaboradores.hijos_registrados],
          ['Con género', d.colaboradores.con_genero],
        ]} />
        <div className="mt-3 flex max-w-lg flex-col gap-2">
          <Cobertura texto="Cobertura de empresa" pct={d.colaboradores.cobertura_empresa_pct} />
          <Cobertura texto="Cobertura de sede" pct={d.colaboradores.cobertura_sede_pct} />
          <Cobertura texto="Cobertura de cargo" pct={d.colaboradores.cobertura_cargo_pct} />
        </div>
      </Grupo>

      <Grupo icono="bi-tags" titulo="Catálogos maestros">
        <Cifras datos={[
          ['Empresas', d.catalogos.empresas],
          ['Sedes', d.catalogos.sedes],
          ['Cargos', d.catalogos.cargos],
          ['Enumeraciones', d.catalogos.enumeraciones],
        ]} />
      </Grupo>

      <Grupo icono="bi-folder2-open" titulo="Expediente digital">
        <Cifras datos={[
          ['Sub-dominios', d.expediente.subdominios],
          ['Registros', d.expediente.registros],
          ['Colaboradores con expediente', d.expediente.colaboradores_con_expediente],
        ]} />
      </Grupo>

      <Grupo icono="bi-megaphone" titulo="Comunicaciones">
        <Cifras datos={[
          ['Total', d.comunicaciones.total],
          ['Enviadas', d.comunicaciones.enviadas],
          ['Programadas', d.comunicaciones.programadas],
          ['Borradores', d.comunicaciones.borradores],
          ['Destinatarios alcanzados', d.comunicaciones.destinatarios_alcanzados],
        ]} />
        <p className="mt-2 text-2 text-muted">
          Por canal — <i className="bi bi-megaphone" aria-hidden="true" /> Muro:{' '}
          {d.comunicaciones.por_canal.feed} · <i className="bi bi-envelope" aria-hidden="true" />{' '}
          Correo: {d.comunicaciones.por_canal.email} ·{' '}
          <i className="bi bi-bell" aria-hidden="true" /> Buzón: {d.comunicaciones.por_canal.notif}
        </p>
      </Grupo>

      <Grupo icono="bi-robot" titulo="Automatizaciones">
        <Cifras datos={[
          ['Reglas', d.automatizaciones.reglas],
          ['Activas', d.automatizaciones.reglas_activas],
          ['Eventos', d.automatizaciones.eventos],
          ['Procesados', d.automatizaciones.procesados],
          ['Pendientes', d.automatizaciones.pendientes],
          ['Errores', d.automatizaciones.errores],
        ]} />
      </Grupo>

      <Grupo icono="bi-cloud-arrow-up" titulo="Importaciones">
        <Cifras datos={[
          ['Lotes', d.importaciones.total],
          ['Creados', d.importaciones.creados],
          ['Actualizados', d.importaciones.actualizados],
        ]} />
        {d.importaciones.ultima && (
          <p className="mt-2 text-2 text-muted">
            Última: {Utils.fmtDatetime(d.importaciones.ultima)}
          </p>
        )}
      </Grupo>

      <Grupo icono="bi-shield-check" titulo="Trazabilidad">
        <Cifras datos={[
          ['Operaciones', d.trazabilidad.operaciones],
          ...Object.entries(d.trazabilidad.por_source).map(
            ([fuente, n]) => [`Auditoría · ${fuente}`, n] as [string, number]),
        ]} />
      </Grupo>
    </div>
  );
}

function Grupo({ icono, titulo, children }: {
  icono: string;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 flex items-center gap-2 text-3 font-bold text-brand-ink">
        <i className={`bi ${icono}`} aria-hidden="true" /> {titulo}
      </h2>
      {children}
    </Card>
  );
}

function Cifras({ datos }: { datos: Array<[string, number]> }) {
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {datos.map(([etiqueta, n]) => (
        <div key={etiqueta} className="rounded-sm border border-line bg-sunken p-3">
          <dd className="text-7 font-extrabold leading-none tabular-nums text-brand-ink">{n}</dd>
          <dt className="mt-1 text-2 text-muted">{etiqueta}</dt>
        </div>
      ))}
    </dl>
  );
}

/** Una cobertura del catálogo. El porcentaje va en tinta legible y no en el
 *  color del semáforo: el dato tiene que leerse igual sin distinguir colores. */
function Cobertura({ texto, pct }: { texto: string; pct: number }) {
  return (
    <div>
      <p className="flex justify-between text-2 text-muted">
        <span>{texto}</span>
        <strong className="tabular-nums text-ink">{pct} %</strong>
      </p>
      <BarraDeProgreso pct={pct} etiqueta={texto} />
    </div>
  );
}
